import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CustomersFacade } from '@domain/application/facade';
import { SessionService } from '@domain/application/observable-services/session.service';
import { MonitorService } from '@domain/infrastructure/api/monitor/monitor.service';
import { DocumentType } from '@domain/model/enums/document-type/document-type.enum';
import { RouterService } from '@modules/business-accounts/core/router/router.service';
import { IBaseFormBuilder } from '@modules/business-accounts/forms/utils/builders/base-builder.class';
import { ConfigNaturalPersonBuilder } from '@modules/business-accounts/forms/utils/config-step/builders/config-natural-person-builder.class';
import { ConfigurationStepDirector } from '@modules/business-accounts/forms/utils/config-step/director.class';
import { DataCollectionFormBuilder } from '@modules/business-accounts/forms/utils/data-collection/builders/data-collection-builder.class';
import { DataCollectionpDirector } from '@modules/business-accounts/forms/utils/data-collection/director.class';
import { NaturalPersonDataClientFormBuilder } from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-client-builder.class';
import { NaturalPersonDataNonClientFormBuilder } from '@modules/business-accounts/forms/utils/data-step/natural-person/builders/natural-person-data-non-client-builder.class';
import { NaturalPersonDataStepDirector } from '@modules/business-accounts/forms/utils/data-step/natural-person/director.class';
import { OtpBuilder } from '@modules/business-accounts/forms/utils/otp/builders/otp-builder.class';
import { OtpStepDirector } from '@modules/business-accounts/forms/utils/otp/director.class';
import { AuthService } from '@modules/business-accounts/services/auth/auth.service';
import { ConfigContextService } from '@modules/business-accounts/services/config-context/config-context.service';
import { ParametersService } from '@modules/business-accounts/services/parameters/parameters.service';
import { TraceService } from '@modules/business-accounts/services/trace/trace.service';
import { TrackingService } from '@modules/business-accounts/services/tracking/tracking.service';
import { VisitEvent } from '@modules/business-accounts/utils/classes/visit-event.class';
import { MAX_NUMBER_ACCOUNTS } from '@modules/business-accounts/utils/constants/user-workflow-constants';
import { TraceError } from '@modules/business-accounts/utils/enums/trace-erros.enum';
import { TraceEventName } from '@modules/business-accounts/utils/enums/trace-events.enum';
import { AccountCreatedEvent } from '@modules/business-accounts/utils/interfaces/account-created-event.interface';
import { IBusinessAccountsConfig } from '@modules/business-accounts/utils/interfaces/business-accounts-wc.config';
import { BARoutes } from '@utils/constants/routes/cmp-link';
import { CustomerReniecResponse, ICustomerResponse } from '@utils/interfaces/response';
import { catchError, delay, filter, map, mergeMap, Observable, of, switchMap, tap, timeout } from 'rxjs';
import { IContainer } from '../utils/interfaces/container.interface';
import { DEFAULT_VIEW_CONFIG, ViewConfig } from '../utils/types/view-config.type';

@Component({
  selector: 'ba-natural-person-container',
  templateUrl: './natural-person-container.component.html',
  styleUrls: ['./natural-person-container.component.scss'],
})
export class NaturalPersonContainerComponent extends BARoutes() implements OnInit, IContainer {
  @Input() documentNumber!: string;
  @Input() companyDocumentNumber!: string;
  @Input() documentType!: Exclude<DocumentType, DocumentType.RUC>;

  @Output() userBlacklisted = new EventEmitter();
  @Output() userMaxAccountsNumber = new EventEmitter();
  @Output() viewConfigChange = new EventEmitter<ViewConfig>();
  @Output() processCompleted = new EventEmitter<AccountCreatedEvent[]>();
  @Output() flowCompleted = new EventEmitter();
  @Output() exitFlowCreateAccount = new EventEmitter<void>();

  currentStep$!: Observable<number>;
  customer$!: ReturnType<CustomersFacade['fetchCustomer']>;
  company$!: ReturnType<CustomersFacade['fetchCompany']>;
  customerId: string | null = null;
  companyId: string | null = null;

  isClient: boolean = true;
  firstWindow: string = 'send-email';
  accountsNumber: number = 0;

  configForm?: FormGroup;
  dataForm?: FormGroup;
  otpForm?: FormGroup;
  dataCollectionForm?: FormGroup;

  showErrorDisclaimer = false;
  showWarnDisclaimer = false;
  isButtonDisabled = false;
  isConfigutarionStepLoading = false;
  isInlineConfigActive: boolean = false;
  internetBanking: boolean = false;
  config$!: Observable<IBusinessAccountsConfig | null>;

  constructor(
    public readonly viewRouter: RouterService,
    private readonly host: ElementRef<HTMLDivElement>,
    private readonly customersFacade: CustomersFacade,
    private trackingService: TrackingService,
    private traceService: TraceService,
    private readonly sessionService: SessionService,
    private readonly authService: AuthService,
    private readonly parametersService: ParametersService,
    private configContext: ConfigContextService,
    private monitorService: MonitorService
  ) {
    super();
  }

  ngOnInit(): void {
    this.callFetchAccountsCounter();
    this.viewRouter.route$
      .pipe(
        filter(() => !!this.dataForm),
        filter((route) => route === this.baRoutes.BUSINESS_DATA)
      )
      .subscribe((route) => {
        const { departmentId, provinceId, districtId } = this.dataForm?.get('miscData')?.value;
        if (departmentId) this.dataForm?.get('personalData')?.get('department')?.setValue(departmentId);
        if (provinceId) this.dataForm?.get('personalData')?.get('province')?.setValue(provinceId);
        if (districtId) this.dataForm?.get('personalData')?.get('district')?.setValue(districtId);
      });
  }

  callFetchAccountsCounter() {
    if (this.documentNumber != '' && this.companyDocumentNumber != '') {
      this.customer$ = this.customersFacade
        .fetchAccountsCounter()
        .pipe(
          catchError(() => {
            return of({ businessAccounts: MAX_NUMBER_ACCOUNTS });
          }),
          tap(({ businessAccounts }) => {
            if (businessAccounts >= MAX_NUMBER_ACCOUNTS) this.userMaxAccountsNumber.emit();

            this.accountsNumber = businessAccounts;
          }),
          filter(({ businessAccounts }) => businessAccounts != MAX_NUMBER_ACCOUNTS),
          switchMap(() => this.customersFacade.fetchBlackList(this.documentNumber, this.documentType))
        )
        .pipe(
          tap((response) => {
            if (response) this.userBlacklisted.emit();
          }),
          filter((isInBlackList) => !isInBlackList),
          switchMap(() => this.customersFacade.fetchCompany(this.companyDocumentNumber))
        )
        .pipe(
          catchError(() => {
            return of({ company: { companyId: null } });
          }),
          tap(({ company: { companyId } }) => (this.companyId = companyId))
        )
        .pipe(
          switchMap(() => this.customersFacade.fetchCustomer(this.documentNumber, this.documentType)),
          catchError((err) => {
            this.isClient = false;
            this.traceService.setCommonInfo({
              isClient: false,
              isNaturalPerson: true
            });
            this.initializeCurrentStep(err);
            throw err;
          }),
          tap(({ customer: { id } }) => {
            this.customerId = id;
            this.traceService.setCommonInfo({
              isClient: true,
              isNaturalPerson: true
            });
          }),
          tap((customer) => this.initializeCurrentStep(customer))
        );
    } else this.initializeCurrentStep();
  }

  initializeDataForm({ customer }: ICustomerResponse) {
    if (this.dataForm) return;
    let builder: IBaseFormBuilder;
    if (this.isClient) {
      builder = new NaturalPersonDataClientFormBuilder(this.sessionService, {
        ruc: this.companyDocumentNumber,
      });
    } else {
      builder = new NaturalPersonDataNonClientFormBuilder(this.sessionService, {
        ruc: this.companyDocumentNumber,
      });
    }
    const director = new NaturalPersonDataStepDirector(builder);
    director.make();
    this.dataForm = builder.getResult() as FormGroup;
  }

  initializeConfigForm() {
    const builder: IBaseFormBuilder = new ConfigNaturalPersonBuilder();
    const director = new ConfigurationStepDirector(builder);

    const dataFormValue = this.dataForm?.value || {};
    dataFormValue.documentNumber = this.documentNumber;
    dataFormValue.documentType = this.documentType;
    dataFormValue.companyDocumentNumber = this.companyDocumentNumber;
    dataFormValue.customerId = this.customerId;
    dataFormValue.companyId = this.companyId;
    dataFormValue.isClient = this.isClient;
    dataFormValue.accountsNumber = this.accountsNumber;

    director.make(dataFormValue);
    this.configForm = builder.getResult() as FormGroup;
  }

  initializeOtpForm() {
    const builder: IBaseFormBuilder = new OtpBuilder();
    const director = new OtpStepDirector(builder);

    director.make();
    this.otpForm = builder.getResult() as FormGroup;
  }

  initializeDataCollectionForm() {
    const builder: IBaseFormBuilder = new DataCollectionFormBuilder();
    const director = new DataCollectionpDirector(builder);

    director.make();
    this.dataCollectionForm = builder.getResult() as FormGroup;
  }

  requestScrollTop() {
    this.host.nativeElement.dispatchEvent(
      new CustomEvent('scrollRequest', {
        detail: { dir: 'top' },
        bubbles: true,
      })
    );
  }

  initializeCurrentStep(initializeForm?: ICustomerResponse) {
    this.currentStep$ = this.viewRouter.route$.pipe(
      map((route) => {
        this.requestScrollTop();
        switch (route) {
          case this.baRoutes.DATA_COLLECTION:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_DATA_COLLECTION));
            this.initializeDataCollectionForm();
            return 0;
          case this.baRoutes.STARTUP:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_INITIAL));
            this.viewConfigChange.emit({
              showTitle: false,
              showBackButton: false,
              showDivider: false,
              disableStickyNav: true,
              showLogo: true,
            });
            return 1;
          case this.baRoutes.AUTHORIZE:
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Validación de identidad',
            });
            return 2;
          case this.baRoutes.BUSINESS_DATA:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_PERSONAL_DATA));
            this.initializeDataForm(initializeForm!);
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Tus datos personales',
              showBackButton: false,
            });
            return 3;
          case this.baRoutes.OTP:
            this.initializeOtpForm();
            return 4;
          case this.baRoutes.CONFIGURATION:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_ACCOUNT_DATA));
            this.initializeConfigForm();
            this.viewConfigChange.emit({
              ...DEFAULT_VIEW_CONFIG,
              titleText: 'Configura tu nueva cuenta',
            });
            return 5;
          case this.baRoutes.SUCCESS:
            this.traceService.registerEvent(new VisitEvent(TraceEventName.PAGE_RENDER_SUCCESS));
            this.viewConfigChange.emit({
              showTitle: false,
              showBackButton: false,
              showDivider: false,
              disableStickyNav: true,
              showLogo: true,
            });
            return -1;
          default:
            return 1;
        }
      })
    );
  }

  onStartupStepDone() {
    this.viewRouter.navigate(this.baRoutes.AUTHORIZE);
  }

  onAuthorizationStepDone() {
    if (this.isClient)
      this.fetchReniecData(this.documentNumber);
    else
      this.viewRouter.navigate(this.baRoutes.BUSINESS_DATA);
  }

  onDataStepDone() {
    if (this.isClient)
      this.viewRouter.navigate(this.baRoutes.CONFIGURATION);
    else
      this.viewRouter.navigate(this.baRoutes.OTP);
  }

  onOtpStepDone() {
    if (this.isClient)
      this.viewRouter.navigate(this.baRoutes.BUSINESS_DATA);
    else
      this.viewRouter.navigate(this.baRoutes.CONFIGURATION);
  }

  onDataCollectionDone() {
    const { documentNumber } = this.sessionService.getCommonInfoValue;
    this.config$ = this.configContext.config$.pipe(tap((config) => (this.isInlineConfigActive = !!config?.inline)));
    this.config$.subscribe((obj: IBusinessAccountsConfig | null) => {
      this.authService
        .populateHeaders({
          sessionToken: obj?.sessionToken!,
          subscriptionKey: obj?.subscriptionKey!,
          documentType: DocumentType.DNI,
          documentNumber: documentNumber,
        })
        .subscribe({
          next: () => { this.callFetchAccountsCounter(); this.parametersService.populateParameters(); this.monitorService.startMonitorData(); },
          error: () => {
            // this.authError = true;
            this.traceService.registerError(TraceError.AUTH_ERROR);
          },
        });
    });
    this.viewRouter.navigate(this.baRoutes.STARTUP);
  }

  onConfigurationStepDone() {
    this.isConfigutarionStepLoading = true;
    const {
      companyData: company,
      configData: { currency, tariff, bieAccess },
      customerData: customer,
      storeData: store,
      agencyData: agency,
    } = this.configForm?.value;

    const payload: any = {
      currency,
      bieAccess,
      tariff,
      store,
      company,
      agency: { code: agency.departmentCode, name: agency.department },
      customer: {
        id: customer.id,
        ldpd: { accepted: this.configForm?.value?.configData?.personalData || false },
        phone: [customer.phone],
        email: [customer.email],
        pep: {
          accepted: this.dataForm?.value.personalData.isPEP || false,
          pepEntity: this.dataForm?.value.personalData.pepLegalRepresentative?.pepEntity || '',
          pepPosition: this.dataForm?.value.personalData.pepLegalRepresentative?.pepPosition || '',
          pepStartDate: this.dataForm?.value.personalData.pepLegalRepresentative?.pepStartDate || null,
          pepEndDate: this.dataForm?.value.personalData.pepLegalRepresentative?.pepEndDate || null,
        }
      },
    };

    if (!this.isClient) {
      payload.customer = {
        firstName: customer.firstName,
        lastName: customer.lastName,
        motherLastName: customer.motherLastName,
        gender: customer.gender,
        birthDate: customer.birthDate,
        maritalStatus: customer.maritalStatus,

        identityDocument: [customer.identityDocument],
        email: [customer.email],
        phone: [customer.phone],
        address: [{ ...customer.address, country: 'PE' }],
        ldpd: { accepted: this.configForm?.value?.configData?.personalData || false },
        pep: {
          accepted: this.dataForm?.value.personalData.isPEP || false,
          pepEntity: this.dataForm?.value.personalData.pepLegalRepresentative?.pepEntity || '',
          pepPosition: this.dataForm?.value.personalData.pepLegalRepresentative?.pepPosition || '',
          pepStartDate: this.dataForm?.value.personalData.pepLegalRepresentative?.pepStartDate || null,
          pepEndDate: this.dataForm?.value.personalData.pepLegalRepresentative?.pepEndDate || null,
        }
      };
    }

    payload.deviceType = this.trackingService.getDeviceInfo();

    const subscription = this.customersFacade
      .createBussinessAccount(payload)
      .pipe(
        catchError((err) => {
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          throw err;
        }),
        delay(3000),
        mergeMap((res: any) => this.customersFacade.getCorrelationStatus(res.correlationId)),
        timeout(600000),
        catchError((err) => {
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          throw err;
        })
      )
      .subscribe((response: any) => {
        if (response.correlation.status === 'SETTLED') {

          if (response.correlation.data.accounts) {

            const accountsInfo: AccountCreatedEvent[] = [];

            response.correlation.data.accounts.forEach((account: any) => {
              accountsInfo.push({
                currency: account.currency,
                accountId: account.id,
                status: response.correlation.status,
                accountCorrelationId: response.correlation.id
              });

            });
            this.internetBanking = bieAccess;
            this.processCompleted.emit(accountsInfo);
            this.viewRouter.navigate(this.baRoutes.SUCCESS);
            subscription.unsubscribe();
          }
        }

        if (response.correlation.status === 'FAILED') {
          this.showErrorDisclaimer = true;
          this.isButtonDisabled = true;
          this.isConfigutarionStepLoading = false;
          subscription.unsubscribe();
        }
      });
  }

  onSuccessStepDone() {
    this.dataForm?.reset({ personalData: { ruc: this.companyDocumentNumber } });
    this.flowCompleted.emit();
    this.viewRouter.navigate(this.baRoutes.STARTUP);
  }

  fetchReniecData(documentNumber: string) {
    this.customersFacade.fetchReniecData(documentNumber).pipe(
      tap(({ customer }: CustomerReniecResponse) => {
        const emails = customer.email.map((item) => item.value);
        if (emails.length > 0)
          this.viewRouter.navigate(this.baRoutes.OTP);
        else
          this.viewRouter.navigate(this.baRoutes.BUSINESS_DATA);
      })
    ).subscribe();
  }
}
